import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";

import {
    getMessagesByChat,
    sendTextMessage,
    sendFileMessage,
    sendVoiceMessage,
    shareEntityToChat,
    markMessagesAsRead,
    deleteMessage,
    receiveMessage,
    deleteMessageSocket,
    markMessagesAsReadSocket,
} from "../../actions/message";

import { setActiveChatIdLocal } from "../../actions/chatActions";
import { markNotificationRead } from "../../actions/notificationActions";
import { socket, joinSocketUser } from "../../socket/socket";
import { playDing } from "../../utils/notifyAudio";
import { openFloatingChat } from "../../utils/openFloatingChat";
import { acquireChatRoom, releaseChatRoom } from "../../utils/chatRoomMembership";
import {
    clearChatViewportState,
    isChatMessageActuallySeen,
    isPageEngaged,
    setChatViewportState,
} from "../../utils/chatVisibility";

import HeaderChatUI from "./HeaderChatUI";
import ChatMessages from "./ChatMessages";
import InputChatUI from "./InputChatUI";

const getId = (value) => String(value?._id || value || "");

const CHAT_BACKGROUND_STORAGE_PREFIX = "chat-background:v1:";
const DEFAULT_CHAT_BACKGROUND = { preset: "default", image: null };

const readChatBackground = (chatId) => {
    if (!chatId || typeof window === "undefined") return DEFAULT_CHAT_BACKGROUND;

    try {
        const raw = window.localStorage.getItem(`${CHAT_BACKGROUND_STORAGE_PREFIX}${chatId}`);
        if (!raw) return DEFAULT_CHAT_BACKGROUND;

        const parsed = JSON.parse(raw);
        const preset = ["default", "soft", "dots", "grid", "custom"].includes(parsed?.preset)
            ? parsed.preset
            : "default";

        return {
            preset,
            image: preset === "custom" && typeof parsed?.image === "string"
                ? parsed.image
                : null,
        };
    } catch (_error) {
        return DEFAULT_CHAT_BACKGROUND;
    }
};

const getNotificationChatId = (notification) => (
    notification?.meta?.chatId ||
    notification?.meta?.targetChatId ||
    notification?.chatId ||
    notification?.targetChatId ||
    null
);

const getNotificationOfficeId = (notification) => (
    notification?.office?._id ||
    notification?.office?.id ||
    notification?.office ||
    notification?.officeId ||
    notification?.meta?.officeId ||
    null
);

const getReplyPayload = (message) => {
    if (!message) return null;

    const sender = message.sender || {};
    const author = message.guestSender?.name ||
        [sender.firstName, sender.lastName].filter(Boolean).join(" ").trim() ||
        sender.email ||
        "Собеседник";
    const snippet = (message.text || message.entity?.title || message.fileName || "Вложение")
        .trim()
        .slice(0, 180);

    return { messageId: message._id, author, snippet };
};

export default function ChatCore({
    chat,
    officeId,
    office,
    role,
    chats = [],
    focusMessageId = null,
    onForwardRequest = null,
    onOpenForwardOrigin = null,
    onBack = null,
    realtimeManaged = false,
    mode = "default",
    onTypingUsersChange = null,
}) {
    const dispatch = useDispatch();
    const chatId = chat?._id;

    const { user } = useSelector((state) => state.auth || {}, shallowEqual);
    const {
        messages = [],
        loading = false,
        sending = false,
        sendingProgress = 0,
        sendError = null,
        nextCursorByChat = {},
        hasMoreByChat = {},
        loadingOlderByChat = {},
    } = useSelector((state) => state.message || {}, shallowEqual);

    const notifications = useSelector(
        (state) => state.notification?.notifications || [],
        shallowEqual
    );

    const userId = user?._id || user?.id;
    const nextCursor = nextCursorByChat?.[chatId] || null;
    const hasMore = !!hasMoreByChat?.[chatId];
    const loadingOlder = !!loadingOlderByChat?.[chatId];

    const [text, setText] = useState("");
    const [file, setFile] = useState(null);
    const [replyTo, setReplyTo] = useState(null);
    const [selectedId, setSelectedId] = useState(null);
    const [typingUsers, setTypingUsers] = useState([]);
    const [showJumpToBottom, setShowJumpToBottom] = useState(false);
    const [newMessagesCount, setNewMessagesCount] = useState(0);
    const [selectedMessageIds, setSelectedMessageIds] = useState([]);
    const [selectionLimitHit, setSelectionLimitHit] = useState(false);
    const [chatBackground, setChatBackground] = useState(() => readChatBackground(chatId));

    const scrollRef = useRef(null);
    const inputRef = useRef(null);
    const atBottomRef = useRef(true);
    const shouldScrollRef = useRef(false);
    const typingTimerRef = useRef(null);
    const markReadTimerRef = useRef(null);
    const loadingOlderRef = useRef(false);
    const realtimeManagedRef = useRef(realtimeManaged);
    const roomOwnerRef = useRef(
        `chat-core-room-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
    );
    const chatViewportInstanceRef = useRef(
        `chat-view-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
    );

    useEffect(() => {
        realtimeManagedRef.current = realtimeManaged;
    }, [realtimeManaged]);

    useEffect(() => {
        setChatBackground(readChatBackground(chatId));
    }, [chatId]);

    const handleChatBackgroundChange = useCallback((nextBackground) => {
        if (!chatId) return false;

        const preset = ["default", "soft", "dots", "grid", "custom"].includes(nextBackground?.preset)
            ? nextBackground.preset
            : "default";
        const normalized = {
            preset,
            image: preset === "custom" && typeof nextBackground?.image === "string"
                ? nextBackground.image
                : null,
        };

        try {
            if (typeof window !== "undefined") {
                window.localStorage.setItem(
                    `${CHAT_BACKGROUND_STORAGE_PREFIX}${chatId}`,
                    JSON.stringify(normalized)
                );
            }
            setChatBackground(normalized);
            return true;
        } catch (_error) {
            return false;
        }
    }, [chatId]);

    useEffect(() => {
        onTypingUsersChange?.(typingUsers);
    }, [typingUsers, onTypingUsersChange]);

    useEffect(() => {
        return () => {
            onTypingUsersChange?.([]);
        };
    }, [onTypingUsersChange]);

    const chatMessages = useMemo(() => (
        (messages || []).filter((message) => getId(message.chatId) === getId(chatId))
    ), [messages, chatId]);

    const selectedMessages = useMemo(() => {
        const selectedSet = new Set(selectedMessageIds.map(getId));
        return chatMessages.filter((message) => selectedSet.has(getId(message?._id)));
    }, [chatMessages, selectedMessageIds]);

    const unreadChatNotifications = useMemo(() => (
        (notifications || []).filter((notification) => (
            !notification?.isRead &&
            getId(getNotificationChatId(notification)) === getId(chatId)
        ))
    ), [notifications, chatId]);

    const syncViewportState = useCallback((atBottom = atBottomRef.current) => {
        if (!chatId) return;

        setChatViewportState({
            instanceId: chatViewportInstanceRef.current,
            chatId,
            atBottom,
        });
    }, [chatId]);

    const scrollToBottom = useCallback((behavior = "auto") => {
        const el = scrollRef.current;
        if (!el) return;

        if (behavior === "smooth" && typeof el.scrollTo === "function") {
            el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
        } else {
            el.scrollTop = el.scrollHeight;
        }

        atBottomRef.current = true;
        syncViewportState(true);
        setShowJumpToBottom(false);
        setNewMessagesCount(0);
    }, [syncViewportState]);

    const scrollToMessage = useCallback((messageId) => {
        if (!messageId) return false;
        const el = document.getElementById(`chat-message-${messageId}`);
        if (!el) return false;

        el.scrollIntoView({ block: "center", behavior: "smooth" });
        return true;
    }, []);

    const requestScroll = useCallback(() => {
        shouldScrollRef.current = true;
    }, []);

    const scheduleMarkRead = useCallback(() => {
        if (!chatId || !userId) return;

        // Read receipt отправляем только когда пользователь реально видит чат:
        // вкладка активна, окно в фокусе и список находится у нижней границы.
        if (!isChatMessageActuallySeen(chatId)) return;

        if (markReadTimerRef.current) clearTimeout(markReadTimerRef.current);

        markReadTimerRef.current = setTimeout(() => {
            // Повторная проверка важна: за 450 мс пользователь мог переключить
            // вкладку/окно или уйти вверх по истории.
            if (!isChatMessageActuallySeen(chatId)) {
                markReadTimerRef.current = null;
                return;
            }

            dispatch(markMessagesAsRead(chatId, userId));

            // Notification и message read должны двигаться вместе. Для текущего
            // чата закрываем все уже полученные непрочитанные notification items.
            unreadChatNotifications.forEach((notification) => {
                const notificationId = notification?._id || notification?.id;
                if (!notificationId) return;

                dispatch(markNotificationRead(notificationId, {
                    scope: notification.scope || "personal",
                    officeId: getNotificationOfficeId(notification) || undefined,
                }));
            });

            markReadTimerRef.current = null;
        }, 450);
    }, [dispatch, chatId, userId, unreadChatNotifications]);

    const loadOlderMessages = useCallback(async () => {
        const el = scrollRef.current;

        if (!el || !chatId || !nextCursor || !hasMore || loadingOlder || loadingOlderRef.current) return;

        loadingOlderRef.current = true;
        const prevHeight = el.scrollHeight;
        const prevTop = el.scrollTop;

        await dispatch(getMessagesByChat(chatId, {
            before: nextCursor,
            limit: 30,
            append: true,
        }));

        requestAnimationFrame(() => {
            const target = scrollRef.current;
            if (target) target.scrollTop = target.scrollHeight - prevHeight + prevTop;
            loadingOlderRef.current = false;
        });
    }, [dispatch, chatId, nextCursor, hasMore, loadingOlder]);

    useLayoutEffect(() => {
        if (focusMessageId && scrollToMessage(focusMessageId)) {
            shouldScrollRef.current = false;
            return;
        }

        if (!shouldScrollRef.current) return;
        shouldScrollRef.current = false;
        scrollToBottom();
    }, [chatMessages.length, focusMessageId, scrollToBottom, scrollToMessage]);

    useEffect(() => {
        if (!chatId) return undefined;

        setText("");
        setFile(null);
        setReplyTo(null);
        setSelectedId(null);
        setTypingUsers([]);
        setShowJumpToBottom(false);
        setNewMessagesCount(0);
        setSelectedMessageIds([]);
        setSelectionLimitHit(false);
        atBottomRef.current = true;
        syncViewportState(true);

        if (mode !== "floating") {
            dispatch(setActiveChatIdLocal(chatId));
        }

        let cancelled = false;

        const loadInitialMessages = async () => {
            if (!focusMessageId) requestScroll();

            const firstPage = await dispatch(getMessagesByChat(chatId, {
                limit: focusMessageId ? 100 : 50,
                append: false,
            }));

            if (cancelled || !focusMessageId) return;

            const focusId = getId(focusMessageId);
            let found = (firstPage?.messages || []).some(
                (message) => getId(message?._id) === focusId
            );
            let cursor = firstPage?.nextCursor || null;
            let page = 0;

            while (!found && cursor && page < 30 && !cancelled) {
                const older = await dispatch(getMessagesByChat(chatId, {
                    before: cursor,
                    limit: 100,
                    append: true,
                }));

                found = (older?.messages || []).some(
                    (message) => getId(message?._id) === focusId
                );
                cursor = older?.nextCursor || null;
                page += 1;
            }

            if (found && !cancelled) {
                window.requestAnimationFrame(() => {
                    scrollToMessage(focusMessageId);
                });
            }
        };

        loadInitialMessages();

        return () => {
            cancelled = true;
            if (markReadTimerRef.current) clearTimeout(markReadTimerRef.current);
            if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
            markReadTimerRef.current = null;
            typingTimerRef.current = null;
            clearChatViewportState(chatViewportInstanceRef.current);
            if (mode !== "floating") {
                dispatch(setActiveChatIdLocal(null));
            }
        };
    }, [
        dispatch,
        chatId,
        focusMessageId,
        requestScroll,
        scrollToMessage,
        syncViewportState,
        mode,
    ]);

    useEffect(() => {
        if (!chatId || !userId) return;

        const hasUnread = chatMessages.some((message) => {
            if (message.isDeleted) return false;
            if (getId(message.sender) === getId(userId)) return false;
            const readBy = Array.isArray(message.readBy) ? message.readBy.map(getId) : [];
            return !readBy.includes(getId(userId));
        });

        if (hasUnread) scheduleMarkRead();
    }, [chatId, userId, chatMessages, scheduleMarkRead]);

    useEffect(() => {
        if (!chatId || !userId || typeof window === "undefined" || typeof document === "undefined") {
            return undefined;
        }

        const handleEngagementChange = () => {
            if (!isPageEngaged()) {
                if (markReadTimerRef.current) {
                    clearTimeout(markReadTimerRef.current);
                    markReadTimerRef.current = null;
                }
                return;
            }

            scheduleMarkRead();
        };

        document.addEventListener("visibilitychange", handleEngagementChange);
        window.addEventListener("focus", handleEngagementChange);
        window.addEventListener("blur", handleEngagementChange);

        return () => {
            document.removeEventListener("visibilitychange", handleEngagementChange);
            window.removeEventListener("focus", handleEngagementChange);
            window.removeEventListener("blur", handleEngagementChange);
        };
    }, [chatId, userId, scheduleMarkRead]);

    useEffect(() => {
        if (!chatId || !userId) return undefined;

        const handleNewMessage = (message) => {
            if (getId(message.chatId) !== getId(chatId)) return;

            const mine = getId(message.sender) === getId(userId);
            const shouldStick = atBottomRef.current || mine;

            if (!realtimeManagedRef.current) {
                dispatch(receiveMessage(message, userId));
            }

            if (!mine) {
                scheduleMarkRead();
                if (document.hidden && !realtimeManagedRef.current) playDing();
            }

            if (shouldStick) {
                requestScroll();

                // ChatRealtimeBridge can dispatch the same socket event before
                // this local listener runs. A frame-level fallback guarantees
                // that a user who was already at the bottom stays attached to
                // the newest rendered message.
                requestAnimationFrame(() => {
                    if (atBottomRef.current || mine) {
                        scrollToBottom();
                    }
                });
            } else if (!mine) {
                setShowJumpToBottom(true);
                setNewMessagesCount((count) => count + 1);
            }
        };

        const handleDeleted = (payload) => {
            const messageId = payload?.messageId || payload;
            if (messageId) dispatch(deleteMessageSocket(messageId));
        };

        const handleRead = ({ chatId: readChatId, userId: readUserId }) => {
            if (getId(readChatId) !== getId(chatId)) return;
            dispatch(markMessagesAsReadSocket(chatId, readUserId));
        };

        const handleTypingStart = ({ chatId: typingChatId, user: typingUser }) => {
            if (getId(typingChatId) !== getId(chatId)) return;
            if (!typingUser?._id || getId(typingUser._id) === getId(userId)) return;

            setTypingUsers((prev) => (
                prev.some((item) => getId(item._id) === getId(typingUser._id))
                    ? prev
                    : [...prev, typingUser]
            ));
        };

        const handleTypingStop = ({ chatId: typingChatId, userId: typingUserId }) => {
            if (getId(typingChatId) !== getId(chatId)) return;
            setTypingUsers((prev) => prev.filter((item) => getId(item._id) !== getId(typingUserId)));
        };

        joinSocketUser();

        // Каждый ChatCore получает lease на room через общий owner-manager.
        // Если тот же chatId уже держит office bridge или другое окно ChatCore,
        // физический join выполняется один раз, а leave уйдёт только после
        // освобождения последнего владельца.
        acquireChatRoom(chatId, roomOwnerRef.current);

        socket.on("newMessage", handleNewMessage);
        socket.on("messageDeleted", handleDeleted);
        socket.on("messagesRead", handleRead);
        socket.on("typing:start", handleTypingStart);
        socket.on("typing:stop", handleTypingStop);

        return () => {
            releaseChatRoom(chatId, roomOwnerRef.current);
            socket.off("newMessage", handleNewMessage);
            socket.off("messageDeleted", handleDeleted);
            socket.off("messagesRead", handleRead);
            socket.off("typing:start", handleTypingStart);
            socket.off("typing:stop", handleTypingStop);
        };
    }, [dispatch, chatId, userId, requestScroll, scheduleMarkRead, scrollToBottom]);

    const handleScroll = () => {
        const el = scrollRef.current;
        if (!el) return;

        const atBottom = el.scrollHeight - (el.scrollTop + el.clientHeight) < 90;
        atBottomRef.current = atBottom;
        syncViewportState(atBottom);
        setShowJumpToBottom(!atBottom);

        if (atBottom) {
            setNewMessagesCount(0);
            scheduleMarkRead();
        }
        if (el.scrollTop <= 90) loadOlderMessages();
    };

    const emitTyping = () => {
        if (!chatId || !userId) return;

        joinSocketUser();
        socket.emit("typing:start", {
            chatId,
            user: {
                _id: userId,
                firstName: user?.firstName,
                lastName: user?.lastName,
                email: user?.email,
            },
        });

        if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
        typingTimerRef.current = setTimeout(() => {
            socket.emit("typing:stop", { chatId, userId });
        }, 1200);
    };

    const handleSend = async ({ text: messageText, file: messageFile }) => {
        if (!chatId) return false;

        const cleanText = (messageText || "").trim();
        if (!cleanText && !messageFile) return false;

        const replyPayload = replyTo ? {
            messageId: replyTo.messageId,
            author: replyTo.author,
            snippet: replyTo.snippet,
        } : null;

        // Быстрые короткие сообщения отправляем optimistic:
        // UI очищается ДО завершения REST и следующий Enter доступен сразу.
        if (cleanText && !messageFile) {
            setText("");
            setReplyTo(null);
            setSelectedId(null);
            requestScroll();
            setTimeout(() => inputRef.current?.focus?.({ preventScroll: true }), 0);

            dispatch(sendTextMessage(chatId, {
                sender: userId,
                senderUser: user,
                text: cleanText,
                replyTo: replyPayload,
            }));

            return true;
        }

        // Файлы сохраняют последовательную модель: для upload используется
        // единый progress, поэтому параллельные file-upload пока блокируются.
        if (sending) return false;

        const payload = {
            sender: userId,
            text: cleanText,
            replyTo: replyPayload,
        };

        const sent = await dispatch(sendFileMessage(chatId, payload, messageFile));
        if (!sent) return false;

        setText("");
        setFile(null);
        setReplyTo(null);
        setSelectedId(null);
        requestScroll();
        setTimeout(() => inputRef.current?.focus?.({ preventScroll: true }), 0);
        return true;
    };

    const handleShareEntity = async (item) => {
        if (!chatId || !officeId || !item?.type || !item?.id || sending) return false;

        const sent = await dispatch(shareEntityToChat(chatId, {
            type: item.type,
            entityId: item.id,
            officeId,
        }));

        if (!sent) return false;

        requestScroll();
        requestAnimationFrame(() => scrollToBottom());
        setTimeout(() => inputRef.current?.focus?.({ preventScroll: true }), 0);
        return true;
    };

    const handleVoiceFinished = async ({ blob, durationMs }) => {
        if (!blob || !chatId || sending) return false;

        const replyPayload = replyTo ? {
            messageId: replyTo.messageId,
            author: replyTo.author,
            snippet: replyTo.snippet,
        } : null;

        const sent = await dispatch(sendVoiceMessage(
            chatId,
            { sender: userId, durationMs, replyTo: replyPayload },
            blob
        ));

        if (!sent) return false;

        setReplyTo(null);
        setSelectedId(null);
        requestScroll();
        return true;
    };

    const clearMessageSelection = useCallback(() => {
        setSelectedMessageIds([]);
        setSelectionLimitHit(false);
    }, []);

    const addMessageSelection = useCallback((message) => {
        const messageId = getId(message?._id);
        if (!messageId || messageId.startsWith("local:") || message?.isDeleted) return;

        setReplyTo(null);
        setSelectedId(null);
        setSelectedMessageIds((prev) => {
            if (prev.some((id) => getId(id) === messageId)) return prev;
            if (prev.length >= 100) {
                setSelectionLimitHit(true);
                return prev;
            }

            setSelectionLimitHit(false);
            return [...prev, messageId];
        });
    }, []);

    const toggleMessageSelection = useCallback((message) => {
        const messageId = getId(message?._id);
        if (!messageId || messageId.startsWith("local:") || message?.isDeleted) return;

        setReplyTo(null);
        setSelectedId(null);
        setSelectedMessageIds((prev) => {
            const exists = prev.some((id) => getId(id) === messageId);
            if (exists) {
                setSelectionLimitHit(false);
                return prev.filter((id) => getId(id) !== messageId);
            }

            if (prev.length >= 100) {
                setSelectionLimitHit(true);
                return prev;
            }

            setSelectionLimitHit(false);
            return [...prev, messageId];
        });
    }, []);

    const requestForward = useCallback((items) => {
        const normalized = (Array.isArray(items) ? items : [items])
            .filter((message) => message?._id && !String(message._id).startsWith("local:"))
            .slice(0, 100);

        if (!normalized.length) return;

        onForwardRequest?.(normalized, {
            onComplete: clearMessageSelection,
        });
    }, [clearMessageSelection, onForwardRequest]);

    const handleReply = (message) => {
        const payload = getReplyPayload(message);
        setReplyTo(payload);
        setSelectedId(message?._id || null);
        setTimeout(() => inputRef.current?.focus?.({ preventScroll: true }), 0);
    };

    const handleOpenFloating = () => {
        if (!chat?._id || mode === "floating") return;

        openFloatingChat({
            chat,
            officeId: officeId || chat?.officeId?._id || chat?.officeId || null,
            office: office || null,
            role: role || null,
            title: chat?.title || chat?.entity?.title || undefined,
            source: chat?.entity?.type && chat.entity.type !== "none"
                ? {
                    type: chat.entity.type,
                    id: chat.entity.id || chat.entity.entityId || null,
                    label: chat.entity.title || chat.title || "Обсуждение",
                }
                : officeId
                    ? { type: "office", id: officeId, label: office?.name || "Офисный чат" }
                    : { type: "personal", label: "Личный чат" },
        });
    };

    const chatBackgroundClass = `chat-core-AO--bg-${chatBackground?.preset || "default"}`;
    const chatBackgroundStyle = chatBackground?.preset === "custom" && chatBackground?.image
        ? { "--chat-wallpaper-image": `url(${chatBackground.image})` }
        : undefined;

    return (
        <div className={`chat-core-AO ${chatBackgroundClass}`} style={chatBackgroundStyle}>
            {onBack && (
                <button type="button" className="chat-core-AO__mobile-back" onClick={onBack}>
                    ← К списку чатов
                </button>
            )}

            {mode !== "floating" && (
                <HeaderChatUI
                    chat={chat}
                    userId={userId}
                    officeId={officeId}
                    office={office}
                    role={role}
                    typingUsers={typingUsers}
                    onOpenFloating={handleOpenFloating}
                    backgroundPreset={chatBackground?.preset || "default"}
                    onBackgroundChange={handleChatBackgroundChange}
                />
            )}


            <div className="chat-core-AO__messages" ref={scrollRef} onScroll={handleScroll}>
                {loadingOlder && <div className="chat-core-AO__older-loader">Загружаем историю…</div>}

                {loading && !chatMessages.length ? (
                    <div className="chat-core-AO__loader">
                        <span className="chat-core-AO__spinner" />
                        Загрузка сообщений…
                    </div>
                ) : (
                    <ChatMessages
                        messages={chatMessages}
                        currentUserId={userId}
                        selectedId={selectedId}
                        selectedMessageIds={selectedMessageIds}
                        selectionMode={selectedMessageIds.length > 0}
                        chats={chats}
                        focusMessageId={focusMessageId}
                        onReply={handleReply}
                        onDelete={(id) => dispatch(deleteMessage(id, chatId, "user"))}
                        onForward={onForwardRequest ? (message) => requestForward([message]) : null}
                        onOpenForwardOrigin={onOpenForwardOrigin}
                        onSelectMessage={onForwardRequest ? toggleMessageSelection : null}
                        onAddMessageSelection={onForwardRequest ? addMessageSelection : null}
                        officeId={officeId}
                    />
                )}

            </div>

            {showJumpToBottom && (
                <button
                    type="button"
                    className="chat-core-AO__jump-bottom"
                    onClick={() => scrollToBottom("smooth")}
                    title="К последним сообщениям"
                >
                    ↓
                    {newMessagesCount > 0 && <b>{newMessagesCount > 99 ? "99+" : newMessagesCount}</b>}
                </button>
            )}

            {selectedMessages.length > 0 ? (
                <div className="chat-core-AO__selection-bar">
                    <button
                        type="button"
                        className="chat-core-AO__selection-close"
                        onClick={clearMessageSelection}
                        title="Отменить выбор"
                        aria-label="Отменить выбор"
                    >
                        ✕
                    </button>

                    <div className="chat-core-AO__selection-count">
                        <b>Выбрано: {selectedMessages.length}</b>
                        <span>/ 100</span>
                        {selectionLimitHit && <small>Лимит — 100 сообщений</small>}
                    </div>

                    <button
                        type="button"
                        className="chat-core-AO__selection-forward"
                        onClick={() => requestForward(selectedMessages)}
                    >
                        ↗ Переслать
                    </button>
                </div>
            ) : (
                <>
                    {replyTo && (
                        <div className="chat-core-AO__reply">
                            <div className="chat-core-AO__reply-main">
                                <div className="chat-core-AO__reply-title">Ответ</div>
                                <div className="chat-core-AO__reply-author">{replyTo.author}</div>
                                <div className="chat-core-AO__reply-text">{replyTo.snippet}</div>
                            </div>
                            <button
                                type="button"
                                className="chat-core-AO__reply-close"
                                onClick={() => {
                                    setReplyTo(null);
                                    setSelectedId(null);
                                }}
                                aria-label="Отменить ответ"
                            >
                                ✕
                            </button>
                        </div>
                    )}

                    <InputChatUI
                        text={text}
                        setText={(value) => {
                            setText(value);
                            emitTyping();
                        }}
                        file={file}
                        setFile={setFile}
                        inputRef={inputRef}
                        officeId={officeId}
                        onSend={handleSend}
                        onShareEntity={handleShareEntity}
                        onVoiceFinished={handleVoiceFinished}
                        sending={sending}
                        sendingProgress={sendingProgress}
                        sendError={sendError}
                    />
                </>
            )}
        </div>
    );
}
